import { SurveyV2 } from '@act/shared/data-transfer-objects';
import { getSurveyState } from '@act/shared/utilities';
import { Box, List, ListItem, Typography } from '@mui/material';
import { useMemo } from 'react';
import { timeFormatter } from '@act/shared/utilities';

interface SurveyResponseSummaryViewProps {
  assignedSurvey: SurveyV2.AssignedSurveyDto;
}

interface OptionSummary {
  optionText: string;
  isSelected: boolean;
}

type QuestionSummary = {
  questionText: string;
  questionType: string;
  textResponse?: string;
  options?: OptionSummary[];
};

const SurveyResponseSummaryView = ({
  assignedSurvey,
}: SurveyResponseSummaryViewProps) => {
  const questionSummaries = useMemo(() => {
    const { steps } = getSurveyState(
      assignedSurvey.questions,
      assignedSurvey.questionResponses,
      assignedSurvey.surveyVersion?.defaultFinalCheckpoint,
      assignedSurvey.phases
    );

    const questionSummaries: QuestionSummary[] = steps
      .filter(step => step.response)
      .map(({ question, response }) => {
        let options: OptionSummary[] = null;

        if (response.selectedOptionIds) {
          options = question.options.map(option => ({
            optionText: option.text,
            isSelected: response.selectedOptionIds.includes(option.id),
          }));
        }

        let textResponse: string = null;
        if (response.textResponse) {
          if (question.type === 'date') {
            textResponse = timeFormatter.epochToShortDate(
              new Date(response.textResponse.replace('-', '/')).getTime(),
              true
            );
          } else {
            textResponse = response.textResponse;
          }
        }

        return {
          questionText: question.text,
          questionType: question.type,
          textResponse,
          options,
        };
      });

    return questionSummaries;
  }, [assignedSurvey]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        margin: '16px 16px 16px 0',
        padding: '24px',
        background: '#fbfcfe',
        borderRadius: '8px',
        border: '1px solid #dce4e8',
      }}
    >
      {questionSummaries.map(questionSummary => {
        return (
          <Box
            key={questionSummary.questionText}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <Typography
              component="div"
              variant="subheadLarge"
              sx={{ fontWeight: '600' }}
            >
              {questionSummary.questionText}
            </Typography>
            {questionSummary.options.length > 0 && (
              <List sx={{ margin: 0, padding: 0 }}>
                {questionSummary.options.map(option => (
                  <ListItem component="div" key={option.optionText} dense>
                    <Box
                      component="div"
                      sx={{ display: 'inline', width: '16px' }}
                    >
                      {option.isSelected ? '✓ ' : '○'}
                    </Box>
                    <Typography
                      sx={{ fontWeight: option.isSelected ? '600' : '400' }}
                    >
                      {option.optionText}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
            {questionSummary.textResponse && (
              <Typography
                component="div"
                variant="subheadLarge"
                sx={{ paddingLeft: '16px', fontWeight: '400' }}
              >
                {questionSummary.textResponse}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default SurveyResponseSummaryView;
